"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Activity, TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Clock, FileCheck, Flame, Layers, PhoneCall, Pipette, ShieldAlert, ShieldCheck, Sparkles, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const assessmentCriteria = [{
  icon: Activity,
  title: "Hydrostatic Pressure Test",
  description: "We monitor static and dynamic PSI to identify line friction, internal scale accumulation, and pressure drop across multiple simultaneous fixtures."
}, {
  icon: Pipette,
  title: "Water Quality & Particulate Check",
  description: "On-site testing detects dissolved rust, galvanized oxidation particles, and localized pH acidity that accelerates pinhole corrosion."
}, {
  icon: ShieldAlert,
  title: "Material Age & Failure History",
  description: "Assessment of existing polybutylene, galvanized steel, or aging copper runs with documentation of repeat pinhole or joint failures."
}, {
  icon: Layers,
  title: "Acoustic & Thermal Wall Mapping",
  description: "Non-invasive diagnostic tools map supply lines inside wall cavities and slab foundations to determine accessible routing pathways."
}];
const processPhases = [{
  step: "01",
  phase: "Phase 1: Pre-Job Mapping & Preparation",
  duration: "Day 1 (Morning)",
  summary: "Precision site prep to protect flooring, establish clean staging zones, and mark critical fixture penetrations.",
  details: ["Drop cloths and plastic barriers placed along all work pathways", "Thermal scans locate structural studs, wiring, and main supply manifolds", "Temporary bypass supply maintained so the building keeps active water service during daytime hours"]
}, {
  step: "02",
  phase: "Phase 2: Supply Line Routing & Manifold Setup",
  duration: "Day 1 (Afternoon) - Day 2",
  summary: "Installation of new home-run or trunk-and-branch distribution system using premium commercial-grade piping.",
  details: ["New primary shutoff valves and main pressure regulator valve (PRV) installed", "Flexible PEX-A expansion tubing or rigid Type L copper lines pulled through stud bays with minimal drywall openings", "Dedicated home-run lines configured for high-demand fixtures to eliminate pressure fluctuations"]
}, {
  step: "03",
  phase: "Phase 3: Fixture Tie-Ins & Hydro-Testing",
  duration: "Day 2 - Day 3",
  summary: "Final connection to all sinks, showers, toilets, and water heaters followed by high-pressure inspection.",
  details: ["New quarter-turn brass ball stops and braided stainless supply lines fitted at every terminal", "Closed-system pressure hold test at 100+ PSI to certify leak-free integrity before wall closure", "Full system flush to clear residual air, flux, or manufacturing dust"]
}, {
  step: "04",
  phase: "Phase 4: Wall Patching & Code Certification",
  duration: "Day 3 - Day 4",
  summary: "Complete site restoration, local municipal plumbing inspection, and issuance of comprehensive warranty documentation.",
  details: ["Precision sheetrock replacement, taping, mudding, and texture matching on opened cutouts", "Final plumbing permit inspection passed per Jonesboro and Clayton County plumbing codes", "Homeowner walk-through with main shutoff tag labeling and transfer of manufacturer material warranties"]
}];
const materialComparison = [{
  id: "pex",
  name: "PEX-A Expansion Piping",
  tag: "Most Recommended",
  summary: "Cross-linked polyethylene offering optimal flexibility, zero corrosion risk, freeze expansion resistance, and lower overall labor costs.",
  lifespan: "50+ Years",
  corrosionRisk: "Zero (Impervious to acidic water)",
  freezeTolerance: "Expands up to 3x diameter before bursting",
  bestFor: "Full home repipes, retrofit installations, and slab bypasses"
}, {
  id: "copper",
  name: "Type L Hard Copper",
  tag: "Traditional Premium",
  summary: "Rigid metallic piping with high heat tolerance, UV resistance, and natural antibacterial properties, soldered with lead-free brass fittings.",
  lifespan: "50+ Years",
  corrosionRisk: "Moderate (Susceptible to aggressive acidic soils/water)",
  freezeTolerance: "Low (Prone to rigid split fractures)",
  bestFor: "Exposed basement mechanical runs, outdoor risers, and high-heat manifolds"
}];
const faqs = [{
  question: "How long will my water service be turned off during repiping?",
  answer: "We stage every project so you have running water each evening. Water is only shut down in specific zones during active daytime working hours (typically 8:30 AM to 4:30 PM)."
}, {
  question: "Do you have to tear down entire walls to replace pipes?",
  answer: "No. Our technicians use small, surgical wall incisions (typically 4x4 or 6x6 inches) behind cabinets, closets, and low-profile access points. We patch and texture every opening as part of our turnkey scope."
}, {
  question: "When is a whole-home repipe necessary instead of a spot repair?",
  answer: "If you have experienced two or more slab leaks or pinholes within 12 months, or if your home still uses polybutylene or corroded galvanized steel, ongoing spot repairs cost more in the long run than a unified replacement."
}, {
  question: "Is repiping covered by emergency financing or homeowner insurance?",
  answer: "Sudden burst pipe water damage is frequently covered by insurance policies, and we provide itemized engineering reports for adjusters. We also offer flexible low-interest monthly financing plans."
}];
export default function RepipingProcess() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("all-phases");
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="repipingassessmentandprocess" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-18">
          <Badge variant="outline" className="mb-3.5 px-3 py-1 font-semibold text-xs tracking-wider uppercase bg-secondary text-secondary-foreground border-border">
            Engineering-Grade Diagnostics
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight mb-4">
            Repiping Assessment & Phased Replacement Process
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Eliminate recurring pinhole leaks, rusty water, and low pressure.
            Jonesboro Emergency Plumber delivers precision diagnostic assessments
            and structured, low-disruption repiping solutions built to last for
            decades.
          </p>
        </div>

        {/* Diagnostic Assessment Section */}
        <div className="mb-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <span className="text-xs font-bold text-accent-foreground uppercase tracking-wider bg-accent/20 px-2.5 py-1 rounded-sm">
                Step 1: Diagnostics
              </span>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mt-2">
                Comprehensive 4-Point System Assessment
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Before touching a pipe, our master technicians evaluate pressure,
              chemical degradation, and structural line paths.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {assessmentCriteria.map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={item.title} data-index={i} variants={itemVariants}>
                  <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="pb-2">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                        <IconComp className="size-5" />
                      </div>
                      <CardTitle className="text-lg font-default font-semibold text-card-foreground leading-snug">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Phased Process Timeline */}
        <div className="mb-20 bg-secondary/40 rounded-2xl p-6 sm:p-8 md:p-10 border border-border">
          <div className="max-w-2xl mb-8">
            <span className="text-xs font-bold text-primary uppercase tracking-wider bg-primary/10 px-2.5 py-1 rounded-sm">
              Step 2: Execution
            </span>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground mt-2 mb-2">
              Our 4-Phase Clean Replacement Sequence
            </h3>
            <p className="text-sm text-muted-foreground">
              Designed to preserve your daily routine while replacing aged supply
              infrastructure quickly and methodically.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {processPhases.map((phase, idx) => <div key={phase.step} data-index={idx} className="bg-card rounded-xl p-6 border border-border shadow-xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono font-bold bg-primary text-primary-foreground px-2.5 py-1 rounded-md">
                        {phase.step}
                      </span>
                      <h4 className="font-default font-bold text-base sm:text-lg text-card-foreground">
                        {phase.phase}
                      </h4>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3 font-mono">
                    <Clock className="size-3.5 text-accent-foreground" />
                    <span>Estimated: {phase.duration}</span>
                  </div>

                  <p className="text-sm text-card-foreground font-medium mb-4">
                    {phase.summary}
                  </p>

                  <ul className="space-y-2.5 text-xs sm:text-sm text-muted-foreground">
                    {phase.details.map((detail, dIdx) => <li key={dIdx} data-index={dIdx} className="flex items-start gap-2.5">
                        <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                        <span>{detail}</span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>

        {/* Materials & Technical Comparison */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <Badge variant="secondary" className="mb-2">
              Material Engineering
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
              PEX-A vs. Type L Copper: Technical Comparison
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              We guide you through the ideal material specification based on
              local water chemistry, home architecture, and investment goals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {materialComparison.map((mat, i) => <div key={mat.id} data-index={i} className="bg-card border border-border rounded-xl p-6 sm:p-8 flex flex-col justify-between shadow-xs">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-4">
                    <h4 className="text-xl font-default font-bold text-card-foreground">
                      {mat.name}
                    </h4>
                    <Badge variant="default" className="text-xs">
                      {mat.tag}
                    </Badge>
                  </div>

                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                    {mat.summary}
                  </p>

                  <div className="space-y-3 pt-4 border-t border-border text-sm">
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-muted-foreground">Expected Lifespan</span>
                      <span className="font-semibold text-card-foreground">
                        {mat.lifespan}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-muted-foreground">Corrosion Resistance</span>
                      <span className="font-semibold text-card-foreground text-right max-w-[200px]">
                        {mat.corrosionRisk}
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-border/50">
                      <span className="text-muted-foreground">Freeze Tolerance</span>
                      <span className="font-semibold text-card-foreground text-right max-w-[200px]">
                        {mat.freezeTolerance}
                      </span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-muted-foreground">Recommended For</span>
                      <span className="font-semibold text-card-foreground text-right max-w-[200px]">
                        {mat.bestFor}
                      </span>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Technical FAQ Accordion */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-default font-bold text-foreground">
              Frequently Asked Questions About Repiping
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Direct, transparent answers from our Jonesboro master plumbing crew.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`faq-${i}`} data-index={i}>
                <AccordionTrigger className="text-base font-semibold text-foreground hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Emergency Consultation Callout Banner */}
        <div className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 shadow-lg relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <Badge variant="outline" className="bg-primary-foreground/10 text-primary-foreground border-primary-foreground/20 mb-3">
              24/7 Priority Scheduling
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-default font-bold text-primary-foreground mb-3 leading-tight">
              Suspect Old Pipe Failure in Your Jonesboro Property?
            </h3>
            <p className="text-primary-foreground/80 text-sm sm:text-base mb-6 leading-relaxed">
              Do not wait for a catastrophic ceiling collapse or foundation flood.
              Book an on-site pressure assessment and upfront repiping estimate
              today.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="bg-accent text-accent-foreground font-semibold hover:bg-accent/90">
                <Link to="/contact/">
                  Request Repiping Inspection
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground bg-primary/20 hover:bg-primary-foreground/10">
                <a href="tel:4708479076">
                  <PhoneCall className="size-4 mr-2" />
                  Call (470) 847-9076
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}